import React, { useState, useEffect, useMemo } from 'react';
import { BookingFormProps, BookingFormData, TimeSlot, DateSlot } from '@/components/BookingForm/BookingForm.types';
import { 
  BookingFormContainer,
  Header,

  Title,

  TypeButton,

  CalendarContainer,
  CalendarTable,
  CalendarCell,
  CalendarButton,
  NavigationContainer,
  NavigationButton,
  TimeSelectionContainer,
  TimeTable,
  TimeCell,
  TimeButton,
  FormContainer,
  FormTitle,
  FormInput,
  SubmitButton,
  SuccessMessage,
} from '@/components/BookingForm/BookingForm.styles';
import { Group, Divider } from '@mantine/core';

const BookingForm: React.FC<BookingFormProps> = ({ 
  apiUrl = '/api',
  ptoName = 'default',
  services = [],
  onBookingSubmit 
}) => {
  // State management
  const [selectedTypeFace, setSelectedTypeFace] = useState<number | null>(null);
  const [selectedTypeTS, setSelectedTypeTS] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [saved, setSaved] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [blackTime, setBlackTime] = useState<Record<string, boolean>>({});
  
  // Form data
  const [formData, setFormData] = useState<BookingFormData>({
    fio: '',
    inn: '',
    firm: '',
    model: '',
    phone: '',
    email: '',
    timestamp: 0
  });

  // Calendar and time data
  const [dates, setDates] = useState<DateSlot[][]>([]);
  const [times, setTimes] = useState<TimeSlot[][]>([]);

  // Configuration
  const [dayPlus, setDayPlus] = useState(0);
  const [startTime, setStartTime] = useState({ h: 9, m: 0, s: 0 });
  const [endTime, setEndTime] = useState({ h: 17, m: 0, s: 0 });
  const [workDay, setWorkDay] = useState<Record<number, boolean>>({
    1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: false
  });
  const stepMinutes = 60;

  // Computed values
  const getTypeFace = useMemo(() => {
    if (selectedTypeFace === 1) return 'Физ. лицо';
    if (selectedTypeFace !== null) return 'Юр. лицо';
    return '';
  }, [selectedTypeFace]);

  // Create time slots
  const createTimes = () => {
    const d = new Date();
    const d2 = new Date();
    
    d2.setSeconds(endTime.s);
    d2.setHours(endTime.h);
    d2.setMinutes(endTime.m);
    d.setHours(startTime.h);
    d.setMinutes(startTime.m);
    d.setSeconds(startTime.s);

    let b = true;
    let i = 0;
    let rowTimes: TimeSlot[] = [];
    const newTimes: TimeSlot[][] = [];

    while (b) {
      const time: TimeSlot = {
        time: ("0" + d.getHours()).slice(-2) + ":" + ("0" + d.getMinutes()).slice(-2),
        isNotActive: false
      };

      if (blackTime[time.time]) {
        time.isNotActive = true;
      }

      i++;
      if (i > 4) {
        newTimes.push(rowTimes);
        rowTimes = [];
        i = 1;
      }
      rowTimes.push(time);

      d.setTime(d.getTime() + stepMinutes * 60000);
      if (d.getTime() > d2.getTime()) {
        b = false;
      }
    }

    if (i > 0) {
      newTimes.push(rowTimes);
    }

    setTimes(newTimes);
  };

  // Create date calendar
  const createDates = () => {
    const d = new Date();
    d.setDate(1);
    
    if (currentMonth > 0) {
      d.setMonth(d.getMonth() + currentMonth);
    }

    let day = d.getDay();
    if (day === 0) day = 7;
    day = day - 2;
    d.setDate(-day);

    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + dayPlus);

    const newDates: DateSlot[][] = [];

    for (let i = 0; i < 6; i++) {
      const week: DateSlot[] = [];
      for (let i2 = 1; i2 < 8; i2++) {
        const date: DateSlot = {
          date: ("0" + d.getDate()).slice(-2) + "." + 
                ("0" + (d.getMonth() + 1)).slice(-2) + "." + 
                d.getFullYear(),
          isNotActive: true
        };

        if (d.getTime() > currentDate.getTime() && workDay[i2]) {
          date.isNotActive = false;
        }

        week.push(date);
        d.setDate(d.getDate() + 1);
      }
      newDates.push(week);
    }

    setDates(newDates);
  };

  // Calculate date array
  const calcDateArray = () => {
    createDates();
  };

  // Select date and fetch available times
  const selectDate = async (date: string) => {
    try {
      const response = await fetch(`${apiUrl}/singups/flist/${date}/${ptoName}`);
      if (response.ok) {
        setBlackTime({});
        const dt = await response.json();
        if (dt.length > 0) {
          const newBlackTime: Record<string, boolean> = {};
          for (let i = 0; i < dt.length; i++) {
            const tarr = dt[i].datetime.split(" ");
            newBlackTime[tarr[1]] = true;
          }
          setBlackTime(newBlackTime);
        }
        setSelectedDate(date);
        createTimes();
      }
    } catch (error) {
      console.error('Error fetching available times:', error);
    }
  };

  // Select time
  const selectTime = (time: string) => {
    setSelectedTime(time);
  };

  // Select service type
  const selectTypeTS = (type: string) => {
    setSelectedTypeTS(type);
    setDayPlus(0);
    setStartTime({ h: 9, m: 0, s: 0 });
    setWorkDay({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: false });
    setEndTime({ h: 17, m: 0, s: 0 });

    switch (type) {
      case "М2":
        setDayPlus(7);
        setStartTime({ h: 9, m: 0, s: 0 });
        setEndTime({ h: 17, m: 0, s: 0 });
        setWorkDay({ 1: true, 2: false, 3: false, 4: false, 5: true, 6: false, 7: false });
        break;
      case "М3":
        setDayPlus(7);
        setStartTime({ h: 9, m: 0, s: 0 });
        setEndTime({ h: 17, m: 0, s: 0 });
        setWorkDay({ 1: true, 2: false, 3: false, 4: false, 5: true, 6: false, 7: false });
        break;
    }

    setSelectedDate('');
    setSelectedTime('');
    calcDateArray();
  };

  // Select type face
  const selectTypeFace = (type: number) => {
    setSelectedTypeFace(type);
  };

  // Navigation
  const nextMonth = () => {
    setCurrentMonth(prev => prev + 1);
    createDates();
  };

  const previousMonth = () => {
    if (currentMonth > 0) {
      setCurrentMonth(prev => prev - 1);
      createDates();
    }
  };

  // Save booking
  const saveBooking = async () => {
    if (selectedTypeFace === null) return;
const ts=new Date(selectedDate + " " + selectedTime).getTime()/1000;
    const formDataToSend = new FormData();
    
    if (selectedTypeFace === 2) {
      formDataToSend.append("fio", formData.fio);
      formDataToSend.append("firm", formData.firm);
      formDataToSend.append("inn", formData.inn);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("model", formData.model);
      formDataToSend.append("typeface", selectedTypeFace.toString());
      formDataToSend.append("pto", ptoName);
      formDataToSend.append("datetime", selectedDate + " " + selectedTime);
      formDataToSend.append("typets", selectedTypeTS || '');
formDataToSend.append("timestamp", ts.toString());
    } else if (selectedTypeFace === 1) {
      formDataToSend.append("fio", formData.fio);
      formDataToSend.append("firm", "");
      formDataToSend.append("inn", "");
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("model", formData.model);
      formDataToSend.append("typeface", selectedTypeFace.toString());
      formDataToSend.append("pto", ptoName);
      formDataToSend.append("datetime", selectedDate + " " + selectedTime);
      formDataToSend.append("typets", selectedTypeTS || '');
      formDataToSend.append("timestamp", ts.toString());
    }

    try {
      const response = await fetch(`${apiUrl}/singups/add`, {
        method: "POST",
        mode: "cors",
        body: formDataToSend,
      });

      if (response.ok) {
        setSaved(true);
        if (onBookingSubmit) {
          onBookingSubmit({
            typeFace: selectedTypeFace,
            typeTS: selectedTypeTS,
            date: selectedDate,
            time: selectedTime,
            formData
          });
        }
      }
    } catch (error) {
      console.error('Error saving booking:', error);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: keyof BookingFormData, value: string) => {
    setFormData((prev: BookingFormData) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle input change events
  const handleInputChangeEvent = (field: keyof BookingFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(field, e.target.value);
  };

  // Initialize on mount
  useEffect(() => {
    calcDateArray();
  }, [currentMonth, dayPlus, workDay]);

  // Update times when blackTime changes
  useEffect(() => {
    if (selectedDate) {
      createTimes();
    }
  }, [blackTime, selectedDate, startTime, endTime]);

  if (saved) {
    return (
      <BookingFormContainer>
        <SuccessMessage>
          Вы записаны на технический осмотр {selectedDate} {selectedTime}
        </SuccessMessage>
      </BookingFormContainer>
    );
  }

  return (
    <BookingFormContainer>
      <Header>
        <Title>Запись на технический осмотр</Title>
      </Header>

      <Group justify="center" gap="lg" grow m="md">
        <TypeButton
          onClick={() => selectTypeFace(2)}
          $isSelected={selectedTypeFace === 2}
        >
          Юр. лицо
        </TypeButton>
        <TypeButton
          onClick={() => selectTypeFace(1)}
          $isSelected={selectedTypeFace === 1}
        >
          Физ. лицо
        </TypeButton>
      </Group>

      {selectedTypeFace !== null && (
        <>
          <Divider />

        <Group justify="center" gap="lg" grow m="md">
          {services.filter((service: any) => service.view).map((service: any, index: number) => (

            <TypeButton
              key={index}
              onClick={() => selectTypeTS(service.type)}
              $isSelected={selectedTypeTS === service.type}
            >
              {service.name}
            </TypeButton>
          ))}
        </Group>
        </>
      )}

      {selectedTypeTS !== null && (
        <>
          <Divider />

        <CalendarContainer>
          <CalendarTable>
            <tbody>
              {dates.map((week, index) => (
                <tr key={index}>
                  {week.map((day) => (
                    <CalendarCell key={day.date}>
                      <CalendarButton
                        onClick={() => selectDate(day.date)}
                        disabled={day.isNotActive}
                        $isSelected={selectedDate === day.date}
                      >
                        {day.date}
                      </CalendarButton>
                    </CalendarCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </CalendarTable>
          
          <NavigationContainer>
            {currentMonth > 0 && (  
              <NavigationButton onClick={previousMonth}>
               {"<<"} Предыдущий месяц
              </NavigationButton>
            )}
            <NavigationButton onClick={nextMonth}>
              Следующий месяц {">>"}
            </NavigationButton>
          </NavigationContainer>
        </CalendarContainer>
        </>
      )}

      {selectedDate !== '' && (
        <>
          <Divider />

        <TimeSelectionContainer>
          <TimeTable>
            <tbody>
              {times.map((row, index) => (
                <tr key={index}>
                  {row.map((time) => (
                    <TimeCell key={time.time}>
                      <TimeButton
                        onClick={() => selectTime(time.time)}
                        disabled={time.isNotActive}
                        $isSelected={selectedTime === time.time}
                      >
                        {time.time}
                      </TimeButton>
                    </TimeCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </TimeTable>
        </TimeSelectionContainer>
        </>
      )}
      <Divider />

      <FormContainer>
        
        <FormTitle>
          Запись: {getTypeFace} {selectedTypeTS} {selectedDate} {selectedTime}
        </FormTitle>

        {selectedTime !== '' && (
          <>
            <h3>Введите ваши данные:</h3>
            
            {selectedTypeFace === 2 && (
              <>
                <FormInput
                  type="text"
                  placeholder="Название организации"
                  value={formData.firm}
                  onChange={handleInputChangeEvent('firm')}
                />
                <FormInput
                  type="text"
                  placeholder="ФИО представителя"
                  value={formData.fio}
                  onChange={handleInputChangeEvent('fio')}
                />
                <FormInput
                  type="text"
                  placeholder="ИНН"
                  value={formData.inn}
                  onChange={handleInputChangeEvent('inn')}
                />
              </>
            )}

            {selectedTypeFace === 1 && (
              <FormInput
                type="text"
                placeholder="ФИО"
                value={formData.fio}
                onChange={handleInputChangeEvent('fio')}
              />
            )}

            <FormInput
              type="tel"
              placeholder="Телефон"
              value={formData.phone}
              onChange={handleInputChangeEvent('phone')}
            />
            <FormInput
              type="email"
              placeholder="E-mail"
              value={formData.email}
              onChange={handleInputChangeEvent('email')}
            />
            <FormInput
              type="text"
              placeholder="Регистрационный знак"
              value={formData.model}
              onChange={handleInputChangeEvent('model')}
            />
            
            <SubmitButton onClick={saveBooking}>
              Отправить
            </SubmitButton>
          </>
        )}
      </FormContainer>
    </BookingFormContainer>
  );
};

export default BookingForm;
